import { useEffect } from 'react';
import { Navigate, useRoutes, useNavigate } from 'react-router-dom';
import DashboardLayout from '../layouts/dashboard';
import UserPage from '../pages/UserPage';
import LoginPage from '../pages/LoginPage';
import Page404 from '../pages/Page404';
import DashboardAppPage from '../pages/DashboardAppPage';
import SignUpPage from '../pages/SignUpPage';
import LandingRoute from '../landingPage/LandingRoute';
import ForgotPasswordPage from '../pages/ForgotPasswordPage';
import About from '../landingPage/page/About';
import Contact from '../landingPage/page/Contact';
import FAQPage from '../landingPage/page/FAQPage';
import BlogPage from '../landingPage/page/Blog';
import CaseStudiesPage from '../landingPage/page/CaseStudyPage';
import BlogDetailPage from '../landingPage/page/BlogDetailPage';
import CaseStudyDetailPage from '../landingPage/page/CaseStudyDetailPage';
import DocumentDetailPage from '../landingPage/page/DocumentDetailPage';
import PrivacyPolicy from '../landingPage/page/PrivacyPolicy';
import ServiceDeliveryPolicy from '../landingPage/page/ServiceDeliveryPolicy';
import TermsAndConditions from '../landingPage/page/TermsAndConditions';
import LogOut from '../pages/LogOut';
import UpdatePasswordPage from '../pages/UpdatePasswordPage';
import MonitorPage from '../pages/MonitorPage';
import AlertPage from '../pages/AlertPage';
import DomainMonitorPage from '../pages/DomainMonitorPage'
import SubscriptionPage from '../sections/@dashboard/subscription/SubscriptionPage';
import ProfilePage from '../pages/ProfilePage';
import WalletPage from '../pages/WalletPage';
import LinuxOrMacGraphPage from '../pages/LinuxOrMacGraphPage';
import WindowsGraphPage from '../pages/WindowsGraphPage';
import TestimonialPage from '../landingPage/page/TestimonialPage';
import SubscriptionPricingPage from '../pages/SubscriptionPricingPlan';
import ContactSales from '../pages/ContactSales';
import NewsUpdate from '../pages/NewsUpdate';
import { SuccessPage } from '../sections/@dashboard/razorpayPayment';
import DocumentationPage from '../pages/DocumentationPage';
import { isLoggedIn } from './authUtils';
import AdminMonitorPage from '../pages/AdminMonitorPage';
import AdminDomainMonitorPage from '../pages/AdminDomainMonitorPage';

// ----------------------------------------------------------------------

export default function RouterConfig() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn() && window.location.pathname.startsWith('/dashboard')) {
      navigate('/login');
    }
  }, [navigate]);

  const adminRoutes = [
    {
      path: '/',
      element: <LandingRoute />,
      children: [{ element: <Navigate to="/monitoring" />, index: true }],
    },
    {
      path: '/dashboard',
      element: isLoggedIn() ? <DashboardLayout isAdmin /> : <Navigate to="/login" />,
      children: [
        { path: '', element: <Navigate to="/dashboard/admin" /> },
        { path: 'admin', element: <UserPage /> },
        { path: 'admin/monitors', element: <AdminMonitorPage /> },
        { path: 'admin/domain-monitors', element: <AdminDomainMonitorPage /> },
        { path: 'users', element: <UserPage /> },
        { path: 'contact-sales', element: <ContactSales /> },
        { path: 'contact-sales/:userId', element: <ContactSales /> },
        { path: 'news-update', element: <NewsUpdate /> },
        { path: 'profile', element: <ProfilePage /> },
      ],
    },
    {
      path: '/login',
      element: <LoginPage />,
    },
    {
      path: '/logout',
      element: <LogOut />,
    },
    {
      path: '/signup',
      element: <SignUpPage />,
    },
    {
      path: '*',
      element: <Page404 />,
    },
    {
      path: '/forgotPassword',
      element: <ForgotPasswordPage />,
    },
    {
      path: '/updatePassword',
      element: <UpdatePasswordPage />,
    },
    {
      path: '/about',
      element: <About />,
    },
    {
      path: '/contact',
      element: <Contact />,
    },
    {
      path: '/faq',
      element: <FAQPage />,
    },
    {
      path: '/case-studies',
      element: <CaseStudiesPage />,
    },
    {
      path: '/blog',
      element: <BlogPage />,
    },
    {
      path: "/blog/:slug",
      element: <BlogDetailPage />
    },
    {
      path: "/case-studies/:slug",
      element: <CaseStudyDetailPage />
    },
    {
      path: '/document',
      element: <DocumentDetailPage />,
    },
    {
      path: '/testimonial',
      element: <TestimonialPage />,
    },
    {
      path: '/privacy-policy',
      element: <PrivacyPolicy />,
    },
    {
      path: '/service-delivery-policy',
      element: <ServiceDeliveryPolicy />,
    },
    {
      path: '/terms-and-conditions',
      element: <TermsAndConditions />,
    },
  ];

  const userRoutes = [
    {
      path: '/',
      element: <LandingRoute />,
      children: [{ element: <Navigate to="/monitoring" />, index: true }],
    },
    {
      path: '/dashboard',
      element: isLoggedIn() ? <DashboardLayout /> : <Navigate to="/login" />,
      children: [
        { element: <Navigate to="/dashboard/app" />, index: true },
        { path: 'app', element: <DashboardAppPage /> },
        { path: 'monitors', element: <MonitorPage /> },
        { path: 'domainMonitors', element: <DomainMonitorPage /> },
        { path: 'alert', element: <AlertPage /> },
        { path: 'wallet', element: <WalletPage /> },
        { path: 'profile', element: <ProfilePage /> },
        { path: 'subscription', element: <SubscriptionPricingPage /> },
        {
          path: '/dashboard/documentation',
          element: <DocumentationPage />,
        },
        {
          path: '/dashboard/monitorGraph/linuxOrMac/:encodedParams',
          element: <LinuxOrMacGraphPage />,
        },
        {
          path: '/dashboard/monitorGraph/windows/:encodedParams',
          element: <WindowsGraphPage />,
        },
        {
          path: '/dashboard/subscription/:_id',
          element: <SubscriptionPricingPage />,
        },
        {
          path: '/dashboard/subscription/:_id/:subscriptionName/:subscriptionAmount',
          element: <SubscriptionPricingPage />,
        },
        {
          path: '/dashboard/wallet/payment',
          element: <WalletPage />,
        },
        {
          path: '/dashboard/wallet/success',
          element: <SuccessPage />,
        },
      ],
    },
    {
      path: '/dashboard/subscription',
      element: <SubscriptionPage />,
    },
    {
      path: '/dashboard/monitorGraph/linuxOrMac/:encodedParams',
      element: <LinuxOrMacGraphPage />,
    },
    {
      path: '/dashboard/monitorGraph/windows/:encodedParams',
      element: <WindowsGraphPage />,
    },
    {
      path: '/login',
      element: <LoginPage />,
    },
    {
      path: '/signup',
      element: <SignUpPage />,
    },
    {
      path: '/logout',
      element: <LogOut />,
    },
    {
      path: '*',
      element: <Page404 />,
    },
    {
      path: '/forgotPassword',
      element: <ForgotPasswordPage />,
    },
    {
      path: '/updatePassword',
      element: <UpdatePasswordPage />,
    },
    {
      path: '/about',
      element: <About />,
    },
    {
      path: '/contact',
      element: <Contact />,
    },
    {
      path: '/faq',
      element: <FAQPage />,
    },
    {
      path: '/blog',
      element: <BlogPage />,
    },
    {
      path: "/blog/:slug",
      element: <BlogDetailPage />
    },
    {
      path: "/case-studies/:slug",
      element: <CaseStudyDetailPage />
    },
    {
      path: '/case-studies',
      element: <CaseStudiesPage />,
    },
    {
      path: '/document',
      element: <DocumentDetailPage />,
    },
    {
      path: '/testimonial',
      element: <TestimonialPage />,
    },
    {
      path: '/privacy-policy',
      element: <PrivacyPolicy />,
    },
    {
      path: '/service-delivery-policy',
      element: <ServiceDeliveryPolicy />,
    },
    {
      path: '/terms-and-conditions',
      element: <TermsAndConditions />,
    },
  ];

  const routes = useRoutes(localStorage.getItem('role') === 'admin' ? adminRoutes : userRoutes);

  return (
    <>
      {routes}
    </>
  );
}
